import React, { createContext, useContext, useState, useEffect } from 'react';
import API_BASE_URL from '../api/Config';
import toast from 'react-hot-toast';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch wishlist from API on mount
  useEffect(() => {
    const fetchWishlist = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setWishlistItems([]);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/customer/wish-list`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          // Handle HTTP errors (401, 403, 500, etc.)
          if (response.status === 401 || response.status === 403) {
            console.warn('Authentication expired. Clearing wishlist.');
            localStorage.removeItem('authToken');
            setWishlistItems([]);
            return;
          }
          
          const errorData = await response.json().catch(() => ({}));
          console.error('Failed to fetch wishlist:', {
            status: response.status,
            message: errorData.message || response.statusText
          });
          toast.error('Failed to load wishlist. Please try again.', {
            duration: 3000
          });
          setWishlistItems([]);
          return;
        }

        const data = await response.json();
        
        // Handle different API response formats
        let wishlistData = [];
        if (Array.isArray(data)) {
          wishlistData = data;
        } else if (data && typeof data === 'object') {
          // Check common wrapper patterns
          if (Array.isArray(data.wishlist)) {
            wishlistData = data.wishlist;
          } else if (Array.isArray(data.data)) {
            wishlistData = data.data;
          } else if (Array.isArray(data.items)) {
            wishlistData = data.items;
          } else if (Array.isArray(data.products)) {
            wishlistData = data.products;
          }
        }
        
        // Normalize the data to ensure each item has the expected structure
        const normalizedData = wishlistData.map(item => {
          // Handle image field - could be array or string
          let imageUrl = '';
          if (item.image_fullpath && Array.isArray(item.image_fullpath) && item.image_fullpath.length > 0) {
            imageUrl = item.image_fullpath[0];
          } else if (item.image_fullpath && typeof item.image_fullpath === 'string') {
            imageUrl = item.image_fullpath;
          } else if (item.image && Array.isArray(item.image) && item.image.length > 0) {
            imageUrl = item.image[0];
          } else if (item.image && typeof item.image === 'string') {
            imageUrl = item.image;
          } else if (item.product_image) {
            imageUrl = item.product_image;
          } else if (item.thumbnail) {
            imageUrl = item.thumbnail;
          } else if (item.img) {
            imageUrl = item.img;
          }

          return {
            id: item.id || item.product_id || item._id,
            name: item.name || item.product_name || item.title || '',
            price: item.price || item.product_price || 0,
            image: imageUrl,
            description: item.description || item.product_description || '',
            ...item // Keep all other original fields
          };
        });
        
        setWishlistItems(normalizedData);
      } catch (err) {
        // Handle network errors, timeout, etc.
        console.error('Error fetching wishlist:', err);
        toast.error('Network error. Please check your connection and try again.', {
          duration: 3000
        });
        setWishlistItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWishlist();
  }, []);

  const addToWishlist = async (product) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      // User not logged in, redirect to login
      toast('Please log in to add items to your wishlist', {
        icon: 'ℹ️',
        duration: 3000
      });
      window.location.href = '/login';
      return false;
    }

    // Validate product data
    if (!product || !product.id) {
      console.error('Invalid product data:', product);
      toast.error('Invalid product. Please try again.', {
        duration: 3000
      });
      return false;
    }

    // Check if already in wishlist to prevent duplicate toasts
    if (wishlistItems.some((item) => item.id === product.id)) {
      toast('Item already in wishlist', {
        icon: 'ℹ️',
        duration: 2000
      });
      return false;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/customer/wish-list/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          product_ids: [product.id]
        })
      });

      if (!response.ok) {
        // Handle specific HTTP errors
        if (response.status === 401 || response.status === 403) {
          toast.error('Session expired. Please log in again.', {
            duration: 3000
          });
          localStorage.removeItem('authToken');
          window.location.href = '/login';
          return false;
        }

        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to add to wishlist:', {
          status: response.status,
          message: errorData.message || response.statusText
        });
        toast.error(errorData.message || 'Failed to add item to wishlist', {
          duration: 3000
        });
        return false;
      }

      // Add to local state
      setWishlistItems((prevItems) => {
        const existingItem = prevItems.find((item) => item.id === product.id);
        if (existingItem) {
          return prevItems; // Already exists, don't add again
        }
        return [...prevItems, { ...product }];
      });
      
      toast.success('Item added to wishlist', {
        duration: 3000
      });
      return true;
    } catch (err) {
      // Handle network errors, timeout, etc.
      console.error('Error adding to wishlist:', err);
      toast.error('Network error. Please check your connection and try again.', {
        duration: 3000
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('Please log in to manage your wishlist', {
        duration: 3000
      });
      return false;
    }

    // Validate product ID
    if (!productId) {
      console.error('Invalid product ID:', productId);
      toast.error('Invalid product ID. Please try again.', {
        duration: 3000
      });
      return false;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/customer/wish-list/remove`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          product_ids: [productId]
        })
      });

      if (!response.ok) {
        // Handle specific HTTP errors
        if (response.status === 401 || response.status === 403) {
          toast.error('Session expired. Please log in again.', {
            duration: 3000
          });
          localStorage.removeItem('authToken');
          window.location.href = '/login';
          return false;
        }

        if (response.status === 404) {
          // Item already removed, update local state anyway
          console.warn('Item not found on server, removing from local state');
          setWishlistItems((prevItems) => prevItems.filter((item) => item.id !== productId));
          return true;
        }

        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to remove from wishlist:', {
          status: response.status,
          message: errorData.message || response.statusText
        });
        toast.error(errorData.message || 'Failed to remove item from wishlist', {
          duration: 3000
        });
        return false;
      }

      // Remove from local state
      setWishlistItems((prevItems) => prevItems.filter((item) => item.id !== productId));
      toast.success('Item removed from wishlist', {
        duration: 3000
      });
      return true;
    } catch (err) {
      // Handle network errors, timeout, etc.
      console.error('Error removing from wishlist:', err);
      toast.error('Network error. Please check your connection and try again.', {
        duration: 3000
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const isInWishlist = (productId) => {
    return wishlistItems.some((item) => item.id === productId);
  };

  const clearWishlist = () => {
    setWishlistItems([]);
  };

  const getWishlistCount = () => {
    return wishlistItems.length;
  };

  const value = {
    wishlistItems,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    clearWishlist,
    getWishlistCount,
    loading,
  };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};
